import { Component, effect, inject, input, linkedSignal, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucidePen, lucidePlus } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { PaginateurComponent } from '@shared/components/paginateur/paginateur.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { RéférentielsFormComponent } from '../forms/referentiels-form.component';
import { RéférentielFormComponent } from '../forms/nom-form/référentiel-form.component';
import { Référentiel } from '../models/référentiel.model';
import { ConvertReferentielTypeToStringPipe } from '../pipes/convert-referentiel-type-to-string-pipe';
import { ReferentielStore } from '../store/referentiel.store';

@Component({
  selector: 'app-referentiels-list',
  imports: [
    HlmIconImports,
    HlmButtonImports,
    RouterModule,
    PaginateurComponent,
    ConvertReferentielTypeToStringPipe,
    FormsModule,
    HlmSwitchImports,
    HlmCardImports,
    HlmTableImports,
    HlmLabelImports,
    HlmTooltipImports,
  ],
  providers: [provideIcons({ lucidePlus, lucidePen })],
  templateUrl: './referentiels-list.component.html',
})
export class RéférentielsListComponent implements OnInit, OnDestroy {
  readonly referentielStore = inject(ReferentielStore);
  readonly #sidepanelService = inject(SidepanelService);

  readonly cartographie = input.required<Cartographie>();

  readonly referentiels = linkedSignal(() => this.referentielStore.getPaginated());

  constructor() {
    effect(() => this.referentielStore.chargerParCartographie(this.cartographie().id));
  }

  ngOnInit(): void {
    this.referentielStore.reinitialiserFiltres();
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  toggleReferentiel(referentiel: Référentiel) {
    const toastrId = uuidv7();
    this.referentielStore.modifier({
      id: referentiel.id,
      data: {
        estActif: !referentiel.estActif,
      },
      next: () =>
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: `Modification du référentiel réussie`,
            id: toastrId,
          },
          id: toastrId,
        }),
    });
  }

  creerReferentiel() {
    this.#sidepanelService.setComponentToDisplay(
      'Créer un référentiel',
      RéférentielsFormComponent,
      `Pour créer un nouveau référentiel, veuillez choisir son type et lui donner un nom.`,
      { idCartographie: this.cartographie().id },
    );
    this.#sidepanelService.open();
  }

  editerReferentiel(referentiel: Référentiel) {
    this.#sidepanelService.setComponentToDisplay(
      'Éditer le référentiel',
      RéférentielFormComponent,
      `Modifier le nom du référentiel.`,
      { referentiel },
    );
    this.#sidepanelService.open();
  }
}
